/**
 * Page-level GAM key-value targeting derived from the publisher page URL.
 *
 * Strategies (theme.gamUrlParser / GAM_URL_PARSER):
 * - default | empty — sect / sub1 / sub2 (legacy RealGM-style global parser)
 * - path_subs — path segments → sub1…sub4 (e.g. Rtings); no sect, no url
 *
 * All values are truncated to GAM custom-targeting limit (40 chars).
 */

export const GAM_KV_VALUE_MAX_LEN = 40

export type GamUrlParserId = 'default' | 'path_subs' | ''

export type PageUrlTargeting = Record<string, string>

export function truncateKvValue(
  value: string,
  maxLen: number = GAM_KV_VALUE_MAX_LEN
): string {
  if (!value) return ''
  return value.length > maxLen ? value.slice(0, maxLen) : value
}

/** Escape a value for safe injection into generated GPT setTargeting JS strings. */
export function escapeKvForJs(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\r/g, '')
    .replace(/\n/g, '')
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

function hostPartsWithoutWww(hostname: string): string[] {
  const parts = hostname.split('.').filter(Boolean)
  if (parts[0] === 'www') parts.shift()
  return parts
}

function pathSegments(pathname: string): string[] {
  return pathname
    .split('/')
    .filter(Boolean)
    .map(decodeSegment)
}

function withTruncatedValues(kv: PageUrlTargeting): PageUrlTargeting {
  const out: PageUrlTargeting = {}
  for (const [key, value] of Object.entries(kv)) {
    const truncated = truncateKvValue(value)
    if (truncated) out[key] = truncated
  }
  return out
}

/** Legacy: subdomain → sect + path[0]/[1], else path[0]/[1]/[2] as sect/sub1/sub2. */
export function parseDefaultPageSections(href: string): PageUrlTargeting {
  try {
    const url = new URL(href)
    const hostParts = hostPartsWithoutWww(url.hostname)
    const pathParts = pathSegments(url.pathname)

    let sect = ''
    let sub1 = ''
    let sub2 = ''

    if (hostParts.length > 2) {
      sect = hostParts[0]
      sub1 = pathParts[0] || ''
      sub2 = pathParts[1] || ''
    } else {
      sect = pathParts[0] || ''
      sub1 = pathParts[1] || ''
      sub2 = pathParts[2] || ''
    }

    return withTruncatedValues({
      ...(sect ? { sect } : {}),
      ...(sub1 ? { sub1 } : {}),
      ...(sub2 ? { sub2 } : {}),
    })
  } catch {
    return {}
  }
}

/**
 * Path segments map 1:1 to sub1…sub4 (max 4).
 * Example: /laptop/reviews/dell/xps-14-2026 → sub1..sub4
 */
export function parsePathSubs(href: string, maxSubs: number = 4): PageUrlTargeting {
  try {
    const url = new URL(href)
    const pathParts = pathSegments(url.pathname)
    const out: PageUrlTargeting = {}
    const limit = Math.min(maxSubs, 4, pathParts.length)
    for (let i = 0; i < limit; i++) {
      const key = `sub${i + 1}`
      const val = truncateKvValue(pathParts[i])
      if (val) out[key] = val
    }
    return out
  } catch {
    return {}
  }
}

export function parsePageUrlTargeting(
  href: string,
  parser: string | undefined | null
): PageUrlTargeting {
  const id = (parser || 'default').toLowerCase().trim()
  if (id === 'path_subs') {
    return parsePathSubs(href)
  }
  return parseDefaultPageSections(href)
}

/**
 * Build GPT snippet chained off pubads() after disableInitialLoad(), e.g.:
 * googletag.pubads().setTargeting('sub1', ['laptop']).setTargeting('sub2', ['reviews']);
 */
export function buildPageTargetingJs(kv: PageUrlTargeting): string {
  const entries = Object.entries(kv).filter(([, v]) => v)
  if (!entries.length) return ''

  const chain = entries
    .map(([key, value], index) => {
      const escaped = escapeKvForJs(value)
      const call = `.setTargeting('${key}', ['${escaped}'])`
      if (index === 0) {
        return `googletag.pubads()${call}`
      }
      return call
    })
    .join('')

  return chain + ';'
}
